import React from 'react';
import PropTypes from 'prop-types';
import Config from 'config';
let config;
import mediator, { ACTIONS } from 'mediator';
import {
  find,
  findIndex,
  get,
  head,
  memoize,
  some,
  take,
  includes,
  isEmpty,
  isObject,
  isNaN
} from 'lodash';

import API from 'lib/tessitura/API';
import metrics from 'lib/common/metrics';
import features from 'lib/common/features';
import panToElement from 'lib/seatmaps/panToElement';
import processMiniCart from 'lib/cart/processMiniCart';
import excludeLockedItems from 'lib/cart/excludeLockedItems';
import repopulateSubLineItem from 'lib/cart/repopulateSubLineItem';
import pricesForZone from 'lib/pricing/pricesForZone';
import findSeatView from 'lib/seats/findSeatView';
import { findSeatEl } from 'lib/seats/seatFinders';
import logger from 'lib/common/logger';
import {
  checkMaxLimit,
  checkMinLimit,
  checkMultipleLimit
} from 'lib/cart/ticketLimit';

import Modal from 'components/Modal';
import Basket from 'components/Basket';
import SeatDetails from 'components/SeatDetails';

import AdaAttestation from 'components/AdaAttestation';
import { cartContainsRestrictedTickets } from 'lib/cart/ticketRestrictions';

function getCurrentPerformance(performances, id) {
  return find(performances, perf => parseInt(perf.perf_no, 10) === id);
}

class DetailsContainer extends React.Component {
  constructor(props) {
    super(props);
    config = new Config().getConfig();
    this.state = {
      /**
       * The currently selected seat
       */
      selectedSeat: null,

      /**
       * The users preferred price type
       * Set based on their initial selection
       */
      preferredPriceType: null,

      /**
       * Available upsell zones
       */
      upsells: [],

      /**
       * Any notices to be shown to the user
       * e.g. max tickets limit, processing error etc.
       */
      noticeMessage: null,

      /**
       * Is the cart currently processing?
       * Used to lock off UI whilst booking seats.
       */
      isProcessing: false,

      /**
       * Have the tickets been successfully
       * added to the cart?
       */
      isSuccessful: false,

      showAdaAttestation: false,

      adaAttestationSpecialRequests: null
    };

    this.getSeatViewForSelection = this.getSeatViewForSelection.bind(this);
    this.getPriceTypesForSelection = this.getPriceTypesForSelection.bind(this);
    this.findUpsell = this.findUpsell.bind(this);
    this.onSelectUpsell = this.onSelectUpsell.bind(this);
    this.handleSeatSelection = this.handleSeatSelection.bind(this);
    this.handlePriceTypeSelection = this.handlePriceTypeSelection.bind(this);
    this.handleDeselection = this.handleDeselection.bind(this);
    this.handleReselection = this.handleReselection.bind(this);
    this.handleRemoval = this.handleRemoval.bind(this);
    this.handlePriceTypeChange = this.handlePriceTypeChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.showMaxTicketsMessage = this.showMaxTicketsMessage.bind(this);

    this.handleRefreshSeatmap = this.handleRefreshSeatmap.bind(this);
    this.renderEnhancedNotice = this.renderEnhancedNotice.bind(this);
    this.renderSeatDetails = this.renderSeatDetails.bind(this);
    this.renderBasket = this.renderBasket.bind(this);

    this.handleAcceptAdaAttestation = this.handleAcceptAdaAttestation.bind(
      this
    );
    this.handleDeclineAdaAttestation = this.handleDeclineAdaAttestation.bind(
      this
    );
  }

  componentWillMount() {
    mediator.subscribe(ACTIONS.SEAT_SELECTED, this.handleSeatSelection);
  }

  componentDidMount() {
    let currentCartItems = this.props.currentCartItems;

    let lockedPriceTypes;
    if (this.props.isFixedPackagePerformance) {
      lockedPriceTypes = features.getOption('lockedSeats', 'lockedPriceTypes');
    }

    /**
     * Show existing cart items in the mini-cart and on the map.
     */
    if (currentCartItems.length) {
      /**
       * Map cart items into a full object
       * w/ seat data and seat element etc.
       */
      let existingItems = currentCartItems.map(item => {
        return repopulateSubLineItem(
          item,
          this.props.seatData,
          this.props.prices,
          this.props.performances,
          lockedPriceTypes
        );
      });

      /**
       * Mark cart seats as selected in seat-map
       */
      existingItems.forEach(item => {
        if (item && item.customSeatEl) {
          mediator.publish(ACTIONS.SEAT_TOGGLE_SELECTED, {
            seatEl: item.customSeatEl
          });
        } else if (item && item.seatEl) {
          mediator.publish(ACTIONS.SEAT_TOGGLE_SELECTED, {
            seatEl: item.seatEl
          });
        }
      });

      /**
       * Merge existing cart-items with newly selected items
       */
      this.props.setSelectedSeats(
        existingItems
          .concat(this.props.selectedSeats)
          .filter(x => x !== undefined)
      );
    }

    /**
     * Fetch upsells
     */
    if (features.isEnabled('inlineSeatUpsell')) {
      API.SeatUpsells().then(remoteData => {
        if (remoteData) {
          this.setState({
            upsells: remoteData
          });
        }
      });
    }
  }

  componentWillUnmount() {
    mediator.unsubscribe(ACTIONS.SEAT_SELECTED, this.handleSeatSelection);
  }

  getSeatViewForSelection(selection) {
    /* eslint-disable no-empty */
    let seatView;
    try {
      seatView = findSeatView(
        this.props.seatData,
        this.props.seatViews,
        selection
      );
    } catch (e) {}
    return seatView;
  }

  getPriceTypesForSelection(seat) {
    return pricesForZone(this.props.prices, seat.seatData.zone_no);
  }

  findUpsell() {
    if (!features.isEnabled('inlineSeatUpsell')) {
      return;
    }

    const { upsells, selectedSeat } = this.state;

    /**
     * Find the first upsell for the current price-zone
     */
    const upsellDetails = find(upsells, function(upsell) {
      const selectionZone = parseInt(selectedSeat.seatData.zone_no, 10);
      const upsellZone = parseInt(upsell.original_zone_no, 10);
      return selectionZone === upsellZone;
    });

    const upsellMatch =
      upsellDetails &&
      find(this.props.seatData, function(seat) {
        return (
          seat.zone_no == upsellDetails.upgrade_zone_no &&
          seat.seat_status.id == '0'
        );
      });

    /**
     * Get a matching seat from the upsold price zone
     */
    if (upsellMatch) {
      const upsoldSeat = {
        seatEl: findSeatEl(upsellMatch),
        seatData: upsellMatch
      };

      return {
        seat: upsoldSeat,
        seatView: this.getSeatViewForSelection(upsoldSeat),
        prices: take(
          pricesForZone(this.props.prices, upsoldSeat.seatData.zone_no),
          1
        ),
        referencePriceType: head(this.getPriceTypesForSelection(selectedSeat)),
        performanceId: this.props.currentPerformanceId
      };
    } else {
      return;
    }
  }

  onSelectUpsell(upsoldSeat, priceType) {
    const panZoomInstance = this.props.mainPanZoomInstance;

    // Deselect original seat
    this.handleDeselection(this.state.selectedSeat, true);

    // Pan to new seat element
    panToElement(upsoldSeat.seatEl, panZoomInstance, panZoomInstance.getZoom());

    // Select up-sold seat
    mediator.publish(ACTIONS.SEAT_ADDED, upsoldSeat);
    this.handlePriceTypeSelection(upsoldSeat, priceType);
  }

  showMaxTicketsMessage(
    selection,
    lineItems,
    maxTickets,
    isSelectedSeatAlreadyInMiniCart
  ) {
    if (maxTickets === undefined || maxTickets === false || isNaN(maxTickets)) {
      return false;
    }

    let ticketsOverLimit = false;
    let ticketsLimit = isObject(maxTickets) ? maxTickets.default : maxTickets;
    if (maxTickets.zones === undefined) {
      ticketsLimit = maxTickets;
    } else if (maxTickets.zones[0] !== undefined) {
      ticketsLimit = maxTickets.zones[0];
    }

    if (isObject(this.props.maxTickets)) {
      if (maxTickets.zones[selection.seatData.zone_no] !== undefined) {
        let filteredItems = lineItems.filter(
          lineItem => lineItem.seatData.zone_no === selection.seatData.zone_no
        );
        ticketsOverLimit =
          maxTickets.zones[selection.seatData.zone_no] <= filteredItems.length;
      }
    }

    if (ticketsLimit <= lineItems.length) {
      ticketsOverLimit = true;
    }

    return (
      ticketsOverLimit === true &&
      // Don't show the message if we are selecting an existing seat
      isSelectedSeatAlreadyInMiniCart === false
    );
  }

  handleSeatSelection(selection) {
    const { isProcessing } = this.state;
    const { selectedSeats } = this.props;

    if (isProcessing) {
      return;
    }

    /**
     * Is the selection already selected i.e. trying to de-select a seat
     */
    const isSelectedSeatAlreadyInMiniCart = some(selectedSeats, function(item) {
      return item.seatData.seat_no === selection.seatData.seat_no;
    });

    if (
      this.showMaxTicketsMessage(
        selection,
        selectedSeats,
        this.props.maxTickets,
        isSelectedSeatAlreadyInMiniCart
      )
    ) {
      this.props.updategroupSalesNotice(true);
    } else {
      /**
       * Mark the seat as selected on the seat-map.
       */
      mediator.publish(ACTIONS.SEAT_ADDED, selection);

      /**
       * If we have the following:
       * - A preferred price type (we've already selected a seat)
       * - Some items in our mini-cart
       * - We've selected a seat of this type before (zone / hold code / seat type)
       * - We've not already selected the seat (were trying to de-select a seat?)
       * Then try to pre-select preferred price-type otherwise show the seat popup.
       */
      let shouldShowSeatDetails = true;

      let states = {
        noticeMessage: null
      };

      if (shouldShowSeatDetails) {
        states.selectedSeat = selection;
      }

      this.setState(states);
    }
  }

  handlePriceTypeSelection(seat, priceData) {
    const seatSelection = {
      seatEl: seat.seatEl,
      seatData: seat.seatData,
      priceData: priceData,

      //@TODO: Is this still needed?
      performance: getCurrentPerformance(
        this.props.performances,
        this.props.currentPerformanceId
      )
    };

    this.props.setSelectedSeats(
      [seatSelection].concat(this.props.selectedSeats),
      () => {
        metrics.action(
          'Seat Selected',
          `Selected seat ${seatSelection.seatData.seat_num}, row ${
            seatSelection.seatData.seat_row
          }, screen ${seatSelection.seatData.screen_no}`
        );

        this.handleDeselection(seat, false);
      }
    );

    if (!this.state.preferredPriceType) {
      this.setState({ preferredPriceType: seatSelection.priceData.price_type });
    }
  }

  handleDeselection(seat, deselect) {
    this.setState({ selectedSeat: null });
    if (deselect) {
      mediator.publish(ACTIONS.SEAT_DESELECTED, seat);
    }
  }

  handleReselection(lineItem) {
    mediator.publish(ACTIONS.SEAT_SELECTED, lineItem);
  }

  handleRemoval(lineItem) {
    const { isProcessing } = this.state;
    const { selectedSeats } = this.props;

    if (!isProcessing) {
      mediator.publish(ACTIONS.SEAT_DESELECTED, lineItem);
      let idx = findIndex(selectedSeats, lineItem);

      // Remove item without mutating original list
      let newItems = selectedSeats
        .slice(0, idx)
        .concat(selectedSeats.slice(idx + 1));

      this.setState({
        noticeMessage: null
      });

      this.props.setSelectedSeats(newItems, () => {
        metrics.action(
          'Seat Removed',
          `Removed seat ${lineItem.seatData.seat_num}, row ${
            lineItem.seatData.seat_row.seat_row
          }, screen ${lineItem.seatData.screen_no}`
        );
      });
    }
  }

  handlePriceTypeChange(lineItem, priceData) {
    const { isProcessing } = this.state;
    const { selectedSeats } = this.props;

    const currentPerformance = getCurrentPerformance(
      this.props.performances,
      this.props.currentPerformanceId
    );

    if (!isProcessing) {
      const replacementLineItemData = {
        seatEl: lineItem.seatEl,
        seatData: lineItem.seatData,
        priceData: priceData,
        performance: currentPerformance
      };

      // Get the index of the existing item
      const idx = findIndex(selectedSeats, lineItem);

      // Add in the new line item, we are going to mutate the list here.
      const newItems = selectedSeats
        .slice(0, idx)
        .concat(selectedSeats.slice(idx + 1));

      // Insert the new item at the same position as the old item.
      newItems.splice(idx, 0, replacementLineItemData);

      this.setState({
        noticeMessage: null
      });
      this.props.setSelectedSeats(newItems);
    }
  }

  getAdaSeatSelections(processableSeatSelections) {
    return processableSeatSelections.filter(
      x =>
        includes(
          features.getOption('adaAttestation', 'seatTypes'),
          x.seatData.seat_type
        ) ||
        includes(
          features.getOption('adaAttestation', 'holdCodes'),
          x.seatData.hc_no
        )
    );
  }

  hasAlreadyAcceptedAdaAttestation() {
    const { currentPerformanceId } = this.props;
    let alreadyAccepted = false;

    try {
      const adaMeta = localStorage.getItem(
        `PerformanceADAMeta-${currentPerformanceId}`
      );
      if (currentPerformanceId && adaMeta) {
        alreadyAccepted = true;
      }
    } catch (e) {} // eslint-disable-line no-empty

    return alreadyAccepted;
  }

  optionallyStoreAdaData() {
    const { currentPerformanceId } = this.props;
    const { adaAttestationSpecialRequests } = this.state;

    const shouldStore =
      currentPerformanceId && !isEmpty(adaAttestationSpecialRequests);

    if (shouldStore) {
      API.StoreADAData(JSON.stringify(adaAttestationSpecialRequests)).catch(
        function() {
          logger.captureMessage(
            `Failed to store PerformanceADAMeta for ${currentPerformanceId}`
          );
        }
      );
    }
  }

  handleSubmit(e) {
    if (e !== undefined) {
      e.stopPropagation();
    }

    this.setState({
      isProcessing: true
    });

    if (this.props.selectedSeats.length === 0) {
      return this.props.handleRelease(() => {});
    }

    /**
     * Exclude locked items from processing
     */
    let processableLineItems = this.props.selectedSeats.filter(
      item => !item.isLocked
    );

    let currentCartItems = excludeLockedItems(
      this.props.selectedSeats,
      this.props.currentCartItems
    );

    let limitError =
      checkMaxLimit(this.props.priceTypes, this.props.selectedSeats) ||
      checkMinLimit(this.props.priceTypes, this.props.selectedSeats) ||
      checkMultipleLimit(this.props.priceTypes, this.props.selectedSeats);

    let restricted = cartContainsRestrictedTickets(this.props.selectedSeats);

    let requiresAdaAttestation =
      features.isEnabled('adaAttestation') &&
      this.getAdaSeatSelections(processableLineItems).length > 0 &&
      !this.hasAlreadyAcceptedAdaAttestation() &&
      !this.state.adaAttestationSpecialRequests;

    if (requiresAdaAttestation) {
      this.setState({
        showAdaAttestation: true
      });
    } else {
      if (!limitError && !restricted) {
        processMiniCart(
          this.props.currentCart,
          currentCartItems,
          processableLineItems,
          this.props.currentPerformanceId,
          this.props.currentPackageNo,
          this.props.currentFixedPackage
        )
          .then(response => {
            this.setState({
              isSuccessful: true
            });

            this.optionallyStoreAdaData();

            this.props.onComplete.call(null, response);
          })
          .catch(error => {
            this.setState({
              isProcessing: false
            });

            if (error && error.message) {
              this.setState({
                noticeMessage: error.message
              });
            }
          });
      } else {
        this.setState({
          noticeMessage: limitError || restricted,
          isProcessing: false
        });
      }
    }
  }

  handleAcceptAdaAttestation(adaChoices) {
    let adaAttestationSpecialRequests = {};

    if (adaChoices) {
      adaAttestationSpecialRequests = adaChoices;
    }

    this.setState(
      {
        showAdaAttestation: false,
        adaAttestationSpecialRequests: adaAttestationSpecialRequests
      },
      () => {
        this.handleSubmit();
      }
    );
  }

  handleDeclineAdaAttestation() {
    this.setState({
      isProcessing: false,
      showAdaAttestation: false
    });
  }

  /**
   * Handle refreshing the seat map.
   * Used if there was a pottential seat collision.
   * Gives the user the option to refresh the feed and try again.
   */
  handleRefreshSeatmap() {
    if (!this.props.currentPerformanceId) {
      return;
    }

    this.props.setSelectedSeats([], () => {
      metrics.action(
        'Refresh seat availability',
        `Refresh seat availability for performance ${
          this.props.currentPerformanceId
        }`
      );

      this.props.onChangePerformance(this.props.currentPerformanceId);
    });
  }

  renderEnhancedNotice() {
    if (this.state.noticeMessage) {
      return (
        <Modal
          onClose={() => {
            this.setState({
              noticeMessage: null
            });
          }}
        >
          <div className="syos-enhanced-notice">
            <div className="syos-enhanced-notice__inner">
              <h3 className="syos-enhanced-notice__title">
                One or more of your seats could not be reserved
              </h3>
              <div className="syos-enhanced-notice__message">
                <p>
                  This may be because someone has already reserved one of your
                  selected seats. Try{' '}
                  <strong>refreshing the seat availability</strong> or{' '}
                  <strong>selecting other seats</strong>.
                </p>
              </div>
              <div className="syos-enhanced-notice__actions">
                <button
                  className="syos-button"
                  onClick={this.handleRefreshSeatmap}
                >
                  Refresh seat availability
                </button>
              </div>
            </div>
          </div>
        </Modal>
      );
    } else {
      return null;
    }
  }

  renderSeatDetails(selection) {
    /**
     * Check if we have selected a current cart item
     * so we can change the UI as needed.
     */
    let currentLineItem = selection
      ? find(this.props.selectedSeats, function(item) {
          return selection.seatData.seat_no === item.seatData.seat_no;
        })
      : null;

    const canShowSeatDetails = selection && !get(currentLineItem, 'isLocked');

    return canShowSeatDetails ? (
      <SeatDetails
        selectedSeat={selection}
        seatView={this.getSeatViewForSelection(selection)}
        priceTypes={this.getPriceTypesForSelection(selection)}
        currentLineItem={currentLineItem}
        handleSelection={this.handlePriceTypeSelection}
        handleDeselection={this.handleDeselection}
        handleRemoval={this.handleRemoval}
        upsell={this.findUpsell()}
        onSelectUpsell={this.onSelectUpsell}
        details={this.props.details}
        zoneNames={this.props.zoneNames}
      />
    ) : null;
  }

  renderBasket(selectedSeats) {
    let basketButtonLabel = memoize(() => {
      // TODO: Convert to RemoteData style constants.
      if (this.state.isSuccessful) {
        return config.basket.successLabel;
      } else if (this.state.isProcessing) {
        return config.basket.processingLabel;
      } else if (selectedSeats.length === 0) {
        return config.basket.releaseLabel;
      } else if (this.props.currentCartItems.length) {
        return config.basket.updateLabel;
      } else {
        return config.basket.submitLabel;
      }
    });

    let noticeMessage = this.state.noticeMessage;
    let promoCodeMessage = get(this.props.activePromoCode, 'text2');
    if (!noticeMessage && promoCodeMessage) {
      noticeMessage = promoCodeMessage;
    }

    return (
      <Basket
        selectedSeats={selectedSeats}
        prices={this.props.prices}
        buttonLabel={basketButtonLabel()}
        noticeMessage={noticeMessage}
        handleSubmit={this.handleSubmit}
        handleRemoval={this.handleRemoval}
        handleReselection={this.handleReselection}
        handlePriceTypeChange={this.handlePriceTypeChange}
        handleRefreshSeatmap={this.handleRefreshSeatmap}
        isProcessing={this.state.isProcessing}
        details={this.props.details}
        zoneNames={this.props.zoneNames}
      />
    );
  }

  renderAdaAttestation() {
    let canShowAdaAttestation =
      this.state.showAdaAttestation && features.isEnabled('adaAttestation');

    return canShowAdaAttestation ? (
      <AdaAttestation
        handleSelection={this.handleAcceptAdaAttestation}
        handleDeselection={this.handleDeclineAdaAttestation}
        specialRequestOptions={features.getOption(
          'adaAttestation',
          'specialRequestOptions'
        )}
      />
    ) : null;
  }

  render() {
    return (
      <div className="syos-details-container">
        {this.props.renderGroupSalesNotice()}
        {this.renderEnhancedNotice()}
        {this.renderSeatDetails(this.state.selectedSeat)}
        {this.renderBasket(this.props.selectedSeats)}
        {this.renderAdaAttestation()}
      </div>
    );
  }
}

DetailsContainer.propTypes = {
  onComplete: PropTypes.func.isRequired,
  prices: PropTypes.array,
  seatData: PropTypes.array,
  seatViews: PropTypes.array,
  maxTickets: PropTypes.number,
  currentCart: PropTypes.object,
  currentCartItems: PropTypes.array,
  currentFixedPackage: PropTypes.object,
  isFixedPackagePerformance: PropTypes.bool,
  performances: PropTypes.array,
  currentPerformanceId: PropTypes.number,
  currentPackageNo: PropTypes.number,
  mainPanZoomInstance: PropTypes.object,
  activePromoCode: PropTypes.string,
  onChangePerformance: PropTypes.func.isRequired,
  details: PropTypes.object,
  priceTypes: PropTypes.array,
  setSelectedSeats: PropTypes.func,
  selectedSeats: PropTypes.array,
  updategroupSalesNotice: PropTypes.func,
  renderGroupSalesNotice: PropTypes.func,
  handleRelease: PropTypes.func,
  zoneNames: PropTypes.object
};

export default DetailsContainer;



// WEBPACK FOOTER //
// ./src/javascripts/containers/DetailsContainer.js